import React, { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock } from 'lucide-react';
import { TPermissionItem } from '@/data/types';

export interface PermissionGroup {
  id: string;
  label: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  permissions: TPermissionItem[];
}

interface PermissionGroupProps {
  group: PermissionGroup;
  groupIndex: number;
  selectedPermissions: string[];
  onPermissionToggle: (permissionId: string) => void;
  permissionsLocked?: boolean;
}

const PermissionGroupComponent: React.FC<PermissionGroupProps> = memo(({
  group,
  groupIndex,
  selectedPermissions,
  onPermissionToggle,
  permissionsLocked = false,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg"
    >
      {/* Category Header */}
      <div className="mb-4 flex items-center gap-4">
        <div className={`rounded-xl bg-gradient-to-r ${group.color} p-3 text-white shadow-lg`}>
          {group.icon}
        </div>
        <div>
          <h4 className="text-lg font-semibold text-gray-900">{group.title}</h4>
          <p className="text-sm text-gray-600">{group.description}</p>
        </div>
      </div>

      {/* Permissions Grid */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {group.permissions.map((permission, permissionIndex) => {
          const isSelected = selectedPermissions.includes(permission.id);

          return (
            <motion.label
              key={permission.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.15,
                type: 'spring',
                stiffness: 400,
                damping: 25,
              }}
              className={`group relative rounded-xl border-2 p-4 transition-all duration-200 ${
                permissionsLocked
                  ? 'cursor-not-allowed opacity-75'
                  : 'cursor-pointer'
              } ${
                isSelected
                  ? 'border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-md'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white hover:shadow-md'
              }`}
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => !permissionsLocked && onPermissionToggle(permission.id)}
                disabled={permissionsLocked}
                className="sr-only"
              />

              {/* Custom Checkbox Visual */}
              <div className="mb-2 flex items-center justify-between">
                <span
                  className={`text-sm font-semibold transition-colors ${
                    isSelected ? 'text-blue-900' : 'text-gray-900'
                  } ${permissionsLocked ? 'opacity-75' : ''}`}
                >
                  {permission.name}
                </span>

                <div className="flex items-center gap-2">
                  {permissionsLocked && (
                    <Lock className="h-4 w-4 text-amber-600" />
                  )}
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-gray-300 bg-white group-hover:border-blue-300'
                    } ${permissionsLocked ? 'opacity-75' : ''}`}
                  >
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          exit={{ scale: 0, rotate: 180 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                        >
                          <Check className="h-3 w-3" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {permission.description && (
                <p
                  className={`text-xs leading-relaxed transition-colors ${
                    isSelected ? 'text-blue-700' : 'text-gray-600'
                  } ${permissionsLocked ? 'opacity-75' : ''}`}
                >
                  {permission.description}
                </p>
              )}

              {/* Selection Indicator */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                  className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-blue-500"
                />
              )}
            </motion.label>
          );
        })}
      </div>
    </motion.div>
  );
});

PermissionGroupComponent.displayName = 'PermissionGroupComponent';

export default PermissionGroupComponent;
